import {Trans} from '@lingui/macro';
import React, {FC, useRef, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {arrayToggle} from '../../../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {DropdownBox} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBox';
import {DropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItemWithCheckbox} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItemWithCheckbox';
import {PopoverCoordinator} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {CreateFormInputDisplay} from '../../../../../../components/common/createFormInputs/createFormInputDisplay';
import {DropdownItemIcon} from '../../../../../../components/common/dropdowns/dropdownItemIcon';
import {OverflowWithCount} from '../../../../../../components/common/overflow/overflowWithCount';
import {renderedApplicationEntryPointActionFeaturesMap} from '../../../../../../helpers/applicationEntryPointActionHelpers';
import {ApplicationEntryPointActionFeaturesEnum} from '../../../../../../models/api/applicationEntryPointActionModel';

/*
 * Constants.
 */

const chevronWidth = 32; // px.
const dropdownWidth = 225; // px.
const dropdownButtonWidth = 187; // px.

const dropdownOptions = [
  ApplicationEntryPointActionFeaturesEnum.DEVICE_CAMERA,
  ApplicationEntryPointActionFeaturesEnum.DEVICE_CLIPBOARD,
  ApplicationEntryPointActionFeaturesEnum.DEVICE_FULLSCREEN,
  ApplicationEntryPointActionFeaturesEnum.DEVICE_MICROPHONE,
  ApplicationEntryPointActionFeaturesEnum.DISPLAY_CAPTURE,
];

/*
 * Props.
 */

interface DevelopersApplicationInboxSidePanelEntryPointActionFeaturesInputProps {
  features: ReadonlyArray<ApplicationEntryPointActionFeaturesEnum>;
  onSelectedFeaturesChange: HandlerOf<ReadonlyArray<ApplicationEntryPointActionFeaturesEnum>>;
}

/*
 * Style.
 */

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledOverflowDiv = styled.div`
  font-weight: ${fontWeights.bold};
`;

const StyledSeparatorSpan = styled.span`
  padding: 5px 6px;
  background-color: ${(p) => p.theme.greys.shade40};
  border-radius: 16px;
  margin-right: 6px;
`;

const StyledEntryPointActionFeaturesDropdownBox = styled(DropdownBox)`
  width: ${dropdownButtonWidth}px;
`;

/*
 * Component.
 */

export const DevelopersApplicationInboxSidePanelEntryPointActionFeaturesInput: FC<
  DevelopersApplicationInboxSidePanelEntryPointActionFeaturesInputProps
> = (props) => {
  const {features, onSelectedFeaturesChange} = props;
  const [selectedFeatures, setSelectedFeatures] = useState(features);
  const customAnchorRef = useRef<any>(null);

  return (
    <CreateFormInputDisplay
      title={
        <Trans id="developers-application-inbox-side-panel-entry-point-action-features-input-title">
          Components
        </Trans>
      }
      input={
        <PopoverCoordinator
          renderButton={(hasHover) => (
            <StyledEntryPointActionFeaturesDropdownBox
              ref={customAnchorRef}
              hasHover={hasHover}
              noMargin
              boxStyles={{
                width: `${dropdownButtonWidth}px`,
                height: '30px',
                marginTop: 0,
              }}
              value={renderDropdownValue(selectedFeatures, dropdownButtonWidth - chevronWidth)}
              message={
                <Trans id="developers-application-inbox-side-panel-entry-point-action-features-input-message">
                  Select the components your app needs access to
                </Trans>
              }
            />
          )}
          renderModal={(popoverProps) => (
            <SimpleDropdown
              anchor={customAnchorRef}
              onRequestClose={popoverProps.onRequestClose}
              width={dropdownWidth}
            >
              <DropdownItem
                onSelect={(e) => {
                  e.preventDefault();
                  setSelectedFeatures([]);
                  onSelectedFeaturesChange([]);
                }}
                isChecked={selectedFeatures.length === 0}
              >
                <DropdownItemIcon label={null} iconName="noneSmall" />
                <DropdownItemContent isChecked={selectedFeatures.length === 0}>
                  <Trans id="developers-application-inbox-side-panel-entry-point-action-features-none">
                    None
                  </Trans>
                </DropdownItemContent>
              </DropdownItem>
              {dropdownOptions.map((feature) => (
                <SimpleDropdownItemWithCheckbox
                  key={feature}
                  onSelect={(e) => {
                    e.preventDefault();
                    const newFeatures = arrayToggle(selectedFeatures, feature);
                    setSelectedFeatures(newFeatures);
                    onSelectedFeaturesChange(newFeatures);
                  }}
                  isChecked={selectedFeatures.includes(feature)}
                >
                  {renderedApplicationEntryPointActionFeaturesMap[feature]}
                </SimpleDropdownItemWithCheckbox>
              ))}
            </SimpleDropdown>
          )}
        />
      }
    />
  );
};

/*
 * Helpers.
 */

function renderDropdownValue(
  selectedFeatures: ReadonlyArray<ApplicationEntryPointActionFeaturesEnum>,
  overflowMaxWidth: number,
) {
  if (!selectedFeatures || selectedFeatures.length === 0) {
    return (
      <StyledPlaceholderDiv>
        <Trans id="developers-application-inbox-side-panel-entry-point-action-features-input-placeholder">
          None
        </Trans>
      </StyledPlaceholderDiv>
    );
  }

  const renderElement = (value: ApplicationEntryPointActionFeaturesEnum) => (
    <StyledSeparatorSpan>{renderedApplicationEntryPointActionFeaturesMap[value]}</StyledSeparatorSpan>
  );
  const renderOverflowIndicator = (numberOfHiddenFeatures: number) => (
    <StyledOverflowDiv>
      <Trans id="developers-application-inbox-side-panel-entry-point-action-features-input-overflow">
        +{numberOfHiddenFeatures}
      </Trans>
    </StyledOverflowDiv>
  );

  return (
    <OverflowWithCount<ApplicationEntryPointActionFeaturesEnum>
      elements={selectedFeatures}
      renderElement={renderElement}
      renderOverflowIndicator={renderOverflowIndicator}
      overflowContainerWidth={overflowMaxWidth}
    />
  );
}
